import { Grid, Tab, Typography } from '@material-ui/core';
import { CustomTabs } from '@ncr-retail/base-retail-components';
import clsx from 'clsx';
import React, { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { BaseButton } from 'src/common/components/base-button';
import { pushEmeraldModal } from 'src/common/emerald-modal-manager/emerald-modal-manager';
import { CashierPreferenceGroups } from 'src/common/enumerations/cashier-preference-groups';
import { CashierPreferenceLevel, SizeType } from 'src/common/enumerations/cashier-preference-level';
import { CashierPreferences } from 'src/common/interfaces/dtos/CashierPreferences';
import { useResponsiveContext } from 'src/home-page/hooks/responsive-context';
import EmeraldDialog from '../../common/components/emerald-dialog/emerald-dialog';
import ColorBlindnessPreferences from './color-blindness-preferences-pane';
import DominantHandPreferences from './dominant-hand-preferences-pane';
import { styles } from './preferences-styles';
import ZoomLevelPreferences from './zoom-level-preferences-pane';

export const showPreferencesView = (props: CashierPreferencesProps): void => {
  pushEmeraldModal(<CashierPreferencesView {...props} />);
};

const CashierPreferencesView: React.FC<CashierPreferencesProps> = ({
  onSubmit,
  onCancel,
  userPreferences,
}: CashierPreferencesProps): JSX.Element => {
  const classes = styles();
  const { t } = useTranslation();
  const { useSmallFormImages } = useResponsiveContext();

  const [selectedPreferenceGroupIndex, setSelectedPreferenceGroupIndex] = useState<number>(0);
  const [selectedPreferences, setSelectedPreferences] = useState<CashierPreferences>(
    userPreferences,
  );

  const handleChange = (newValue: number): void => {
    setSelectedPreferenceGroupIndex(newValue);
  };

  const updatePreferences = useCallback(
    (prefName: string, newValue: boolean | CashierPreferenceLevel | SizeType): void => {
      setSelectedPreferences(current => ({ ...current, [prefName]: newValue }));
    },
    [setSelectedPreferences],
  );

  const createPreferencePage = (): JSX.Element => {
    switch (selectedPreferenceGroupIndex) {
      case CashierPreferenceGroups.ColorBlindness:
        return (
          <ColorBlindnessPreferences
            userPreferences={selectedPreferences}
            updatePreferences={updatePreferences}
          />
        );
      case CashierPreferenceGroups.ZoomLevel:
        return (
          <ZoomLevelPreferences
            userPreferences={selectedPreferences}
            updatePreferences={updatePreferences}
          />
        );
      default:
        return (
          <DominantHandPreferences
            userPreferences={selectedPreferences}
            updatePreferences={updatePreferences}
          />
        );
    }
  };

  return (
    <EmeraldDialog open fullScreen={true} data-testid='cashier-preferences-dialog'>
      <Grid
        container
        direction='column'
        className={`${classes.cashierPreferencesContainer} fullHeight`}
        data-testid='cashier-preferences-container'
        wrap='nowrap'
      >
        <Grid
          container
          data-testid='cashier-prefrences-title'
          direction='column'
          className={`${classes.cashierPreferencesTitleContainer} fullWidth`}
        >
          <Grid item xs={8}>
            <Grid
              container
              data-testid='cashier-preferences-title-bar-container'
              direction='column'
              className={`${classes.cashierPreferencesTitleBarContainer} fullWidth`}
            >
              <Typography
                className={`${classes.cashierPreferencesTitleHeader} ${classes.cashierPreferencesTitleHeaderLabel}`}
                data-testid='cashier-preferences-title-bar-text'
              >
                Cashier Preferences
              </Typography>
              <div className={clsx(classes.cashierPreferencesBar)} />
            </Grid>
          </Grid>
        </Grid>
        <Grid item className={`${classes.cashierPreferencesTabContainer} fullWidth`}>
          <CustomTabs
            data-testid='preference-group-custom-tabs'
            value={selectedPreferenceGroupIndex}
            onChange={(unused: any, number: number): void => handleChange(number)}
            variant='standard'
            className={classes.customTabs}
            centered
          >
            <Tab
              label='Left/Right Hand Modes'
              className={classes.tab}
              onClick={() => {}}
              data-testid='dominant-hand-tab-button'
            />
            <Tab
              label='Color Blind Modes'
              className={classes.tab}
              onClick={() => {}}
              data-testid='color-blind-mode-tab-button'
            />
            {!useSmallFormImages && (
              <Tab
                label='Button/Text Sizes'
                className={classes.tab}
                onClick={() => {}}
                data-testid='button-size-tab-button'
              />
            )}
          </CustomTabs>
        </Grid>
        <Grid
          container
          direction='column'
          className={classes.cashierPreferencesContainerInner}
          data-testid='cashier-preferences-options-container'
          style={{ marginBottom: 'auto' }}
        >
          {createPreferencePage()}
        </Grid>
        <Grid
          container
          className={classes.cashierPreferencesButtonContainer}
          direction='row'
          alignItems='center'
          justifyContent='flex-start'
        >
          <Grid
            item
            container
            direction='row'
            alignItems='center'
            justifyContent='center'
            spacing={1}
          >
            <Grid item>
              <BaseButton
                onClick={onCancel}
                data-testid='cashier-preferences-cancel-button'
                buttonType='white'
                // TODO: Disabled 'live' changes until design is revisited at different sizes
                // buttonSize={selectedPreferences.buttonZoomLevel}
                // textSize={selectedPreferences.textZoomLevel}
              >
                {t('Cancel')}
              </BaseButton>
            </Grid>
            <Grid item>
              <BaseButton
                onClick={() => onSubmit(selectedPreferences)}
                data-testid='cashier-preferences-submit-button'
                // TODO: Disabled 'live' changes until design is revisited at different sizes
                // buttonSize={selectedPreferences.buttonZoomLevel}
                // textSize={selectedPreferences.textZoomLevel}
              >
                {t('Apply')}
              </BaseButton>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </EmeraldDialog>
  );
};

export interface CashierPreferencesProps {
  userPreferences: CashierPreferences;
  onSubmit: (cashierPreferences: CashierPreferences) => void;
  onCancel: () => void;
}

export default CashierPreferencesView;
